import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { useApiClient } from '@/hooks/useApiClient';
import { useToast } from '@/contexts/ToastContext';
import { extractErrorMessage } from '@/utils/extractErrorMessage';

type Tab = 'create-establishment' | 'create-user' | 'users-list';

export default function AdminCreateUsersPage() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<Tab>('create-establishment');

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-neutral-900">👤 {t('admin.usersTitle')}</h1>
        <p className="text-neutral-600 mt-2">{t('admin.usersSubtitle')}</p>
      </div>

      <div className="flex flex-wrap gap-2 border-b border-neutral-200 pb-0">
        {[
          { key: 'create-establishment' as Tab, label: t('admin.newEstablishment') },
          { key: 'create-user' as Tab, label: t('admin.newUser') },
          { key: 'users-list' as Tab, label: t('admin.allUsers') },
        ].map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`px-4 py-3 text-sm font-medium rounded-t-lg transition-all border-b-2 ${
              activeTab === tab.key
                ? 'border-amber-500 bg-amber-50 text-amber-700'
                : 'border-transparent text-neutral-500 hover:text-neutral-700 hover:bg-neutral-50'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'create-establishment' && <CreateEstablishmentForm />}
      {activeTab === 'create-user' && <CreateUserForm />}
      {activeTab === 'users-list' && <UsersListView />}
    </div>
  );
}

function CreateEstablishmentForm() {
  const { t, i18n } = useTranslation();
  const apiClient = useApiClient();
  const { addToast } = useToast();
  const queryClient = useQueryClient();

  const [form, setForm] = useState({
    name: '',
    ownerEmail: '',
    ownerPassword: '',
    activateSubscription: true,
    durationDays: 365,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [result, setResult] = useState<any>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim() || !form.ownerEmail.trim() || !form.ownerPassword.trim()) {
      addToast(t('admin.fillAllFields'), 'warning');
      return;
    }
    setIsSubmitting(true);
    setResult(null);
    try {
      const res = await (apiClient as any).client.post('/admin/establishments', {
        name: form.name.trim(),
        ownerEmail: form.ownerEmail.trim().toLowerCase(),
        ownerPassword: form.ownerPassword,
        activateSubscription: form.activateSubscription,
        durationDays: form.durationDays,
      });
      setResult(res.data);
      addToast(t('admin.establishmentCreatedToast'), 'success');
      setForm({ name: '', ownerEmail: '', ownerPassword: '', activateSubscription: true, durationDays: 365 });
      queryClient.invalidateQueries({ queryKey: ['admin:'] });
    } catch (err) {
      addToast(extractErrorMessage(err, t('admin.createError')), 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div className="bg-white rounded-xl border border-neutral-200 shadow-sm">
        <div className="px-6 py-4 border-b border-neutral-200 bg-gradient-to-r from-purple-50 to-blue-50">
          <h2 className="font-bold text-neutral-900 flex items-center gap-2">
            <span className="text-xl">🏢</span> {t('admin.createEstablishmentTitle')}
          </h2>
          <p className="text-sm text-neutral-500 mt-1">{t('admin.createEstablishmentDesc')}</p>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          <div>
            <label className="block text-sm font-semibold text-neutral-700 mb-1.5">{t('admin.establishmentName')}</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Ex: Restaurant Le Gourmet"
              className="w-full px-4 py-2.5 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none transition-all"
              disabled={isSubmitting}
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-neutral-700 mb-1.5">{t('admin.ownerEmail')}</label>
            <input
              type="email"
              value={form.ownerEmail}
              onChange={(e) => setForm({ ...form, ownerEmail: e.target.value })}
              placeholder="proprietaire@restaurant.com"
              className="w-full px-4 py-2.5 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none transition-all"
              disabled={isSubmitting}
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-neutral-700 mb-1.5">{t('admin.password')}</label>
            <input
              type="text"
              value={form.ownerPassword}
              onChange={(e) => setForm({ ...form, ownerPassword: e.target.value })}
              className="w-full px-4 py-2.5 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none transition-all font-mono"
              disabled={isSubmitting}
            />
            <p className="text-xs text-neutral-400 mt-1">{t('admin.passwordHint')}</p>
          </div>

          <div className="flex items-center justify-between p-4 bg-green-50 border border-green-200 rounded-lg">
            <div>
              <p className="text-sm font-semibold text-green-800">{t('admin.activateSubscription')}</p>
              <p className="text-xs text-green-600 mt-0.5">{t('admin.activateSubscriptionDesc')}</p>
            </div>
            <button
              type="button"
              onClick={() => setForm({ ...form, activateSubscription: !form.activateSubscription })}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${form.activateSubscription ? 'bg-green-500' : 'bg-neutral-300'}`}
              disabled={isSubmitting}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform shadow-sm ${form.activateSubscription ? 'translate-x-6' : 'translate-x-1'}`} />
            </button>
          </div>

          {form.activateSubscription && (
            <div>
              <label className="block text-sm font-semibold text-neutral-700 mb-1.5">{t('admin.subscriptionDuration')}</label>
              <div className="flex items-center gap-3">
                <input
                  type="number"
                  min={1}
                  max={3650}
                  value={form.durationDays}
                  onChange={(e) => setForm({ ...form, durationDays: Math.max(1, parseInt(e.target.value) || 1) })}
                  className="w-32 px-4 py-2.5 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none transition-all"
                  disabled={isSubmitting}
                />
                <div className="flex gap-2">
                  {[30, 90, 180, 365].map((d) => (
                    <button
                      key={d}
                      type="button"
                      onClick={() => setForm({ ...form, durationDays: d })}
                      className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition-all ${form.durationDays === d ? 'bg-purple-100 border-purple-300 text-purple-700' : 'bg-white border-neutral-200 text-neutral-600 hover:bg-neutral-50'}`}
                      disabled={isSubmitting}
                    >
                      {d === 365 ? '1 an' : d === 180 ? '6 mois' : d === 90 ? '3 mois' : '30j'}
                    </button>
                  ))}
                </div>
              </div>
              <p className="text-xs text-neutral-400 mt-1">
                {t('admin.expiresOn', { date: new Date(Date.now() + form.durationDays * 86400000).toLocaleDateString(i18n.language) })}
              </p>
            </div>
          )}

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full py-3 px-6 bg-gradient-to-r from-purple-600 to-blue-600 text-white font-bold rounded-lg hover:from-purple-700 hover:to-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-md hover:shadow-lg"
          >
            {isSubmitting ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                {t('admin.creating')}
              </span>
            ) : t('admin.createEstablishmentBtn')}
          </button>
        </form>
      </div>

      <div className="space-y-6">
        {result ? (
          <div className="bg-white rounded-xl border border-green-200 shadow-sm">
            <div className="px-6 py-4 border-b border-green-200 bg-green-50">
              <h2 className="font-bold text-green-800 flex items-center gap-2">{t('admin.establishmentCreated')}</h2>
            </div>
            <div className="p-6 space-y-4">
              <InfoRow label={t('admin.establishmentLabel')} value={result.establishment?.name} />
              <InfoRow label={t('admin.establishmentId')} value={result.establishment?.id} mono />
              <InfoRow label={t('admin.ownerId')} value={result.user?.email} />
              <InfoRow label={t('admin.userId')} value={result.user?.id} mono />
              <InfoRow label={t('admin.roleLabel')} value={result.user?.role} />
              <InfoRow label={t('admin.subscriptionLabel')} value={result.subscription?.status} badge />
              <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm font-semibold text-blue-800 mb-2">📧 {t('admin.loginInfo')}</p>
                <p className="text-sm text-blue-700">E-mail : <span className="font-mono font-bold">{result.user?.email}</span></p>
                <p className="text-sm text-blue-700 mt-1">{t('admin.loginAccess')}</p>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-neutral-200 shadow-sm p-4 sm:p-8 text-center">
            <div className="text-5xl mb-4">🏗️</div>
            <h3 className="text-lg font-semibold text-neutral-700 mb-2">{t('admin.readyToCreate')}</h3>
            <p className="text-sm text-neutral-500 max-w-sm mx-auto">{t('admin.readyDesc')}</p>
          </div>
        )}

        <div className="bg-white rounded-xl border border-neutral-200 shadow-sm p-6">
          <h3 className="font-bold text-neutral-800 mb-3">{t('admin.howItWorks')}</h3>
          <ul className="space-y-2 text-sm text-neutral-600">
            {[1, 2, 3, 4].map((n) => (
              <li key={n} className="flex items-start gap-2">
                <span className="text-green-500 mt-0.5">✓</span>
                <span dangerouslySetInnerHTML={{ __html: t(`admin.howItWorks${n}`) }} />
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

function CreateUserForm() {
  const { t } = useTranslation();
  const apiClient = useApiClient();
  const { addToast } = useToast();
  const queryClient = useQueryClient();

  const [form, setForm] = useState({
    email: '',
    password: '',
    role: 'STAFF' as 'STAFF' | 'AUDITOR',
    establishmentId: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [result, setResult] = useState<any>(null);

  const establishmentsQuery = useQuery({
    queryKey: ['admin:establishments-for-user-creation'],
    queryFn: () => apiClient.getAdminEstablishments(0, 200),
  });

  const establishments = establishmentsQuery.data?.data || [];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.email.trim() || !form.password.trim() || !form.establishmentId) {
      addToast(t('admin.fillAllFields'), 'warning');
      return;
    }
    setIsSubmitting(true);
    setResult(null);
    try {
      const res = await (apiClient as any).client.post('/admin/users', {
        email: form.email.trim().toLowerCase(),
        password: form.password,
        role: form.role,
        establishmentId: form.establishmentId,
      });
      setResult(res.data);
      addToast(t('admin.userCreatedToast'), 'success');
      setForm({ email: '', password: '', role: 'STAFF', establishmentId: form.establishmentId });
      queryClient.invalidateQueries({ queryKey: ['admin:'] });
    } catch (err) {
      addToast(extractErrorMessage(err, t('admin.createError')), 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div className="bg-white rounded-xl border border-neutral-200 shadow-sm">
        <div className="px-6 py-4 border-b border-neutral-200 bg-gradient-to-r from-blue-50 to-cyan-50">
          <h2 className="font-bold text-neutral-900 flex items-center gap-2">
            <span className="text-xl">👤</span> {t('admin.addUserTitle')}
          </h2>
          <p className="text-sm text-neutral-500 mt-1">{t('admin.addUserDesc')}</p>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          <div>
            <label className="block text-sm font-semibold text-neutral-700 mb-1.5">{t('admin.selectEstablishment')}</label>
            {establishmentsQuery.isLoading ? (
              <div className="px-4 py-2.5 border border-neutral-200 rounded-lg text-neutral-400 text-sm">{t('admin.loadingEstablishments')}</div>
            ) : (
              <select
                value={form.establishmentId}
                onChange={(e) => setForm({ ...form, establishmentId: e.target.value })}
                className="w-full px-4 py-2.5 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all bg-white"
                disabled={isSubmitting}
              >
                <option value="">{t('admin.selectEstablishmentOption')}</option>
                {establishments.map((est: any) => (
                  <option key={est.id} value={est.id}>
                    {est.name} {est.owner?.email ? `(${est.owner.email})` : ''}
                  </option>
                ))}
              </select>
            )}
          </div>
          <div>
            <label className="block text-sm font-semibold text-neutral-700 mb-1.5">{t('admin.userEmail')}</label>
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="staff@restaurant.com"
              className="w-full px-4 py-2.5 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
              disabled={isSubmitting}
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-neutral-700 mb-1.5">{t('admin.password')}</label>
            <input
              type="text"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              className="w-full px-4 py-2.5 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all font-mono"
              disabled={isSubmitting}
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-neutral-700 mb-1.5">{t('admin.role')}</label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                { value: 'STAFF', label: t('admin.staffRole'), desc: t('admin.staffRoleDesc'), emoji: '👨‍🍳' },
                { value: 'AUDITOR', label: t('admin.auditorRole'), desc: t('admin.auditorRoleDesc'), emoji: '🔍' },
              ].map((role) => (
                <button
                  key={role.value}
                  type="button"
                  onClick={() => setForm({ ...form, role: role.value as any })}
                  disabled={isSubmitting}
                  className={`p-3 rounded-lg border-2 text-left transition-all ${form.role === role.value ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-500/20' : 'border-neutral-200 hover:border-blue-300 hover:bg-blue-50/50'}`}
                >
                  <div className="text-lg">{role.emoji}</div>
                  <p className="text-sm font-semibold text-neutral-800 mt-1">{role.label}</p>
                  <p className="text-xs text-neutral-500">{role.desc}</p>
                </button>
              ))}
            </div>
          </div>
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full py-3 px-6 bg-gradient-to-r from-blue-600 to-cyan-600 text-white font-bold rounded-lg hover:from-blue-700 hover:to-cyan-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-md hover:shadow-lg"
          >
            {isSubmitting ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                {t('admin.creating')}
              </span>
            ) : t('admin.createUserBtn')}
          </button>
        </form>
      </div>

      <div>
        {result ? (
          <div className="bg-white rounded-xl border border-green-200 shadow-sm">
            <div className="px-6 py-4 border-b border-green-200 bg-green-50">
              <h2 className="font-bold text-green-800 flex items-center gap-2">{t('admin.userCreated')}</h2>
            </div>
            <div className="p-6 space-y-4">
              <InfoRow label="E-mail" value={result.email} />
              <InfoRow label="ID" value={result.id} mono />
              <InfoRow label={t('admin.roleLabel')} value={result.role} badge />
              <InfoRow label={t('admin.establishmentLabel')} value={result.establishmentId} mono />
              <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm font-semibold text-blue-800 mb-2">📧 {t('admin.loginInfo')}</p>
                <p className="text-sm text-blue-700">{t('admin.loginAccess')}</p>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-neutral-200 shadow-sm p-4 sm:p-8 text-center">
            <div className="text-5xl mb-4">👤</div>
            <h3 className="text-lg font-semibold text-neutral-700 mb-2">{t('admin.addUserReady')}</h3>
            <p className="text-sm text-neutral-500 max-w-sm mx-auto">{t('admin.addUserReadyDesc')}</p>
          </div>
        )}
      </div>
    </div>
  );
}

function UsersListView() {
  const { t } = useTranslation();
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const { addToast } = useToast();

  const usersQuery = useQuery({
    queryKey: ['admin:all-users'],
    queryFn: () => apiClient.getAdminUsers(),
  });

  const establishmentsQuery = useQuery({
    queryKey: ['admin:establishments-for-list'],
    queryFn: () => apiClient.getAdminEstablishments(0, 200),
  });

  const suspendMutation = useMutation({
    mutationFn: (userId: string) => apiClient.toggleUserSuspended(userId),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['admin:all-users'] });
      addToast(
        data.suspended ? t('admin.suspendSuccess', { email: data.email }) : t('admin.reactivateSuccess', { email: data.email }),
        data.suspended ? 'warning' : 'success',
      );
    },
    onError: (err) => {
      addToast(extractErrorMessage(err, t('admin.suspendError')), 'error');
    },
  });

  const users = usersQuery.data || [];
  const establishments = establishmentsQuery.data?.data || [];

  const estMap = new Map<string, string>();
  establishments.forEach((e: any) => estMap.set(e.id, e.name));

  const roleColors: Record<string, string> = {
    PLATFORM_ADMIN: 'bg-amber-100 text-amber-800 border-amber-200',
    OWNER: 'bg-purple-100 text-purple-800 border-purple-200',
    STAFF: 'bg-blue-100 text-blue-800 border-blue-200',
    AUDITOR: 'bg-cyan-100 text-cyan-800 border-cyan-200',
  };

  if (usersQuery.isLoading) {
    return (
      <div className="bg-white rounded-xl border border-neutral-200 shadow-sm p-4 sm:p-12 text-center">
        <div className="text-neutral-500">{t('admin.loadingUsers')}</div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-neutral-200 shadow-sm overflow-hidden">
      <div className="px-6 py-4 border-b border-neutral-200 flex items-center justify-between">
        <h2 className="font-bold text-neutral-900">{t('admin.allUsersTitle', { count: users.length })}</h2>
        <button
          onClick={() => usersQuery.refetch()}
          className="text-sm text-neutral-500 hover:text-neutral-700 px-3 py-1.5 rounded-lg hover:bg-neutral-100 transition-colors"
        >
          {t('admin.refresh')}
        </button>
      </div>

      {/* Desktop table */}
      <div className="hidden sm:block overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-neutral-200 bg-neutral-50">
              <th className="text-left px-4 py-3 font-semibold text-neutral-700">{t('admin.emailCol')}</th>
              <th className="text-left px-4 py-3 font-semibold text-neutral-700 hidden md:table-cell">{t('admin.nameCol')}</th>
              <th className="text-left px-4 py-3 font-semibold text-neutral-700">{t('admin.roleCol')}</th>
              <th className="text-left px-4 py-3 font-semibold text-neutral-700 hidden lg:table-cell">{t('admin.establishmentCol')}</th>
              <th className="text-left px-4 py-3 font-semibold text-neutral-700">{t('admin.statusCol')}</th>
              <th className="text-right px-4 py-3 font-semibold text-neutral-700">{t('admin.actionsCol')}</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user: any) => (
              <tr key={user.id} className={`border-b border-neutral-100 hover:bg-neutral-50 ${user.suspended ? 'opacity-60' : ''}`}>
                <td className="px-4 py-3 font-medium text-neutral-900">{user.email}</td>
                <td className="px-4 py-3 text-neutral-600 hidden md:table-cell">
                  {user.displayName || <span className="text-neutral-400 italic">—</span>}
                </td>
                <td className="px-4 py-3">
                  <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-semibold border ${roleColors[user.role] || 'bg-neutral-100 text-neutral-600'}`}>
                    {t(`roles.${user.role}`, { defaultValue: user.role })}
                  </span>
                </td>
                <td className="px-4 py-3 text-neutral-600 hidden lg:table-cell">
                  {user.establishmentId
                    ? estMap.get(user.establishmentId) || <span className="font-mono text-xs">{user.establishmentId.substring(0, 8)}...</span>
                    : <span className="text-neutral-400 italic">{t('admin.noneEstablishment')}</span>
                  }
                </td>
                <td className="px-4 py-3">
                  {user.suspended ? (
                    <span className="inline-block px-2.5 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-800 border border-red-200">
                      {t('admin.suspended')}
                    </span>
                  ) : (
                    <span className="inline-block px-2.5 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-800 border border-green-200">
                      {t('admin.statusActive')}
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 text-right">
                  {user.role !== 'PLATFORM_ADMIN' && (
                    <button
                      onClick={() => {
                        if (confirm(user.suspended
                          ? t('admin.confirmReactivate', { email: user.email })
                          : t('admin.confirmSuspend', { email: user.email })
                        )) {
                          suspendMutation.mutate(user.id);
                        }
                      }}
                      disabled={suspendMutation.isPending}
                      className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition-all ${
                        user.suspended
                          ? 'bg-green-50 border-green-200 text-green-700 hover:bg-green-100'
                          : 'bg-red-50 border-red-200 text-red-700 hover:bg-red-100'
                      } disabled:opacity-50`}
                    >
                      {user.suspended ? t('admin.reactivate') : t('admin.suspend')}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="sm:hidden space-y-3 p-4">
        {users.map((user: any) => (
          <div key={user.id} className={`bg-white rounded-lg border border-neutral-200 p-4 space-y-3 ${user.suspended ? 'opacity-60' : ''}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                <p className="font-medium text-neutral-900 truncate">{user.email}</p>
                <p className="text-sm text-neutral-600">{user.displayName || <span className="text-neutral-400 italic">—</span>}</p>
              </div>
              <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-semibold border flex-shrink-0 ${roleColors[user.role] || 'bg-neutral-100 text-neutral-600'}`}>
                {t(`roles.${user.role}`, { defaultValue: user.role })}
              </span>
            </div>
            <div className="space-y-2 text-sm">
              <div>
                <p className="text-xs font-semibold text-neutral-500 uppercase">{t('admin.establishmentCol')}</p>
                <p className="text-neutral-700">
                  {user.establishmentId
                    ? estMap.get(user.establishmentId) || <span className="font-mono text-xs">{user.establishmentId.substring(0, 12)}...</span>
                    : <span className="text-neutral-400 italic">{t('admin.noneEstablishment')}</span>
                  }
                </p>
              </div>
              <div className="flex items-center justify-between">
                <div>
                  {user.suspended ? (
                    <span className="inline-block px-2.5 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-800 border border-red-200">
                      {t('admin.suspended')}
                    </span>
                  ) : (
                    <span className="inline-block px-2.5 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-800 border border-green-200">
                      {t('admin.statusActive')}
                    </span>
                  )}
                </div>
                {user.role !== 'PLATFORM_ADMIN' && (
                  <button
                    onClick={() => {
                      if (confirm(user.suspended
                        ? t('admin.confirmReactivate', { email: user.email })
                        : t('admin.confirmSuspend', { email: user.email })
                      )) {
                        suspendMutation.mutate(user.id);
                      }
                    }}
                    disabled={suspendMutation.isPending}
                    className={`px-3 py-2 text-xs font-medium rounded-lg border transition-all min-h-[44px] flex items-center ${
                      user.suspended
                        ? 'bg-green-50 border-green-200 text-green-700 hover:bg-green-100'
                        : 'bg-red-50 border-red-200 text-red-700 hover:bg-red-100'
                    } disabled:opacity-50`}
                  >
                    {user.suspended ? t('admin.reactivate') : t('admin.suspend')}
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function InfoRow({ label, value, mono, badge }: { label: string; value?: string; mono?: boolean; badge?: boolean }) {
  if (!value) return null;
  const statusColors: Record<string, string> = {
    ACTIVE: 'bg-green-100 text-green-800',
    PAST_DUE: 'bg-yellow-100 text-yellow-800',
    OWNER: 'bg-purple-100 text-purple-800',
    STAFF: 'bg-blue-100 text-blue-800',
    AUDITOR: 'bg-cyan-100 text-cyan-800',
    PLATFORM_ADMIN: 'bg-amber-100 text-amber-800',
  };
  return (
    <div className="flex items-center justify-between py-2 border-b border-neutral-100 last:border-0">
      <span className="text-sm text-neutral-500">{label}</span>
      {badge ? (
        <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${statusColors[value] || 'bg-neutral-100 text-neutral-600'}`}>{value}</span>
      ) : (
        <span className={`text-sm font-medium text-neutral-900 ${mono ? 'font-mono text-xs' : ''}`}>{value}</span>
      )}
    </div>
  );
}
